import React, { useCallback } from "react";

import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { RenderElementProps } from "slate-react/dist/components/editable";

import EmailResourceMetadata from "@mapmycustomers/shared/types/email/EmailResourceMetadata";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import validateEmailResource from "@mapmycustomers/shared/util/email/validateEmailResource";
import formatFileSize from "@mapmycustomers/shared/util/formatters/formatFileSize";

import Button from "../../../../Button";
import useConfigProvider from "../../../../ConfigProvider/utils/useConfigProvider";
import FileIcon, { FileIconFile } from "../../../../FileIcon";
import { ResourceLinkElement } from "../../../../type/slate-types";

import styles from "./ResourceLink.module.scss";

interface ResourceLinkProps {
  attributes: RenderElementProps["attributes"];
  children: React.ReactNode;
  element: ResourceLinkElement;
  onDownload?: () => void;
  onRemoved?: () => void;
  readOnly?: boolean;
  resource?: Resource;
  resourceMetadata?: EmailResourceMetadata;
}

const ResourceLink = ({
  attributes,
  element,
  onDownload,
  onRemoved,
  readOnly,
  resource,
  resourceMetadata,
}: ResourceLinkProps) => {
  const editor = useSlate();
  const configProvider = useConfigProvider();

  const handleDownload = useCallback(() => {
    if (!resource?.file) {
      return;
    }
    onDownload?.();
  }, [resource, onDownload]);

  const handleRemove = useCallback(() => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at: path });
    onRemoved?.();
  }, [editor, element, onRemoved]);

  if (!resource && !resourceMetadata) {
    return null;
  }

  const name = resource?.name || resourceMetadata?.name;

  const fileContentLength = resource?.file?.contentLength ?? resourceMetadata!.fileContentLength;

  const fileIconFile: FileIconFile = resource?.file
    ? { contentType: resource.file.contentType, name: resource.name }
    : { contentType: resourceMetadata!.fileContentType, name: resourceMetadata!.name };

  const { notFound, restricted } = validateEmailResource(resource);

  return (
    <span
      {...attributes}
      className={cn(styles.container, { [styles.danger]: restricted || notFound })}
      contentEditable={false}
    >
      {<FileIcon className={styles.fileIcon} file={fileIconFile} />}
      <span className={styles.name}>
        <span className={styles.namePure}>{name}</span>
        {fileContentLength !== null && (
          <span className={styles.nameExtra}>
            <span>({formatFileSize(fileContentLength)})</span>
          </span>
        )}
      </span>
      <span className={styles.suffix}>
        {restricted || notFound ? (
          <Tag color="error">
            {configProvider.formatMessage(
              restricted
                ? "ui.emailBody.resourceLink.restricted"
                : "ui.emailBody.resourceLink.notFound"
            )}
          </Tag>
        ) : (
          <Tooltip
            title={configProvider.formatMessage("ui.emailBody.resourceLink.downloadTooltip")}
          >
            <Button
              className={styles.downloadButton}
              icon={<FontAwesomeIcon icon={faDownload} />}
              iconOnly
              onClick={handleDownload}
              type="text"
            />
          </Tooltip>
        )}
        {!readOnly && (
          <Tooltip title={configProvider.formatMessage("ui.emailBody.resourceLink.removeTooltip")}>
            <Button
              icon={<FontAwesomeIcon icon={faClose} />}
              iconOnly
              onClick={handleRemove}
              type="control"
            />
          </Tooltip>
        )}
      </span>
    </span>
  );
};

export default ResourceLink;
